import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { authCopyEn } from "@patient-watch/i18n/webapp/en-GB/authCopy";
import { authCopyDe } from "@patient-watch/i18n/webapp/de-DE/authCopy";
import { authCopyDa } from "@patient-watch/i18n/webapp/da-DK/authCopy";
import { authCopyEnUs } from "@patient-watch/i18n/webapp/en-US/authCopy";
import { authCopyFr } from "@patient-watch/i18n/webapp/fr-FR/authCopy";
import { authCopyIt } from "@patient-watch/i18n/webapp/it-IT/authCopy";
import { authCopyEs } from "@patient-watch/i18n/webapp/es-ES/authCopy";

export interface AuthCopy {
    backToWebsite: string;
    languageLabel: string;
    languageDescription: string;
    chooseLanguage: string;
    or: string;
    next: string;
    submit: string;
    continue: string;
    logout: string;
    verifyingAuthentication: string;
    signUpSignIn: string;
    signIn: string;
    signUp: string;
    signUpWith: (name: string) => string;
    alreadyHaveAccount: string;
    dontHaveAccount: string;
    signInWithPasswordInstead: string;
    contactMethodLabel: string;
    patientRole: string;
    forgotPassword: string;
    confirmYourAccount: string;
    choosePassword: string;
    confirmPassword: string;
    showPassword: string;
    passwordsDontMatch: string;
    passwordsMatch: string;
    /** Password strength panel shown while typing: every requirement met. */
    passwordAllGood: string;
    passwordRequirementMinLength: string;
    passwordRequirementLowercase: string;
    passwordRequirementUppercase: string;
    passwordRequirementNumber: string;
    passwordRequirementSpecial: string;
    setYourPassword: string;
    setPasswordDescription: string;
    settingPassword: string;
    smartAuthHint: string;
    usePhoneInstead: string;
    useEmailInstead: string;
    phoneVerificationHint: string;
    phoneSignupHint: string;
    loggingIn: string;
    sendingCode: string;
    logIn: string;
    sendCode: string;
    sendVerificationCode: string;
    sendingCodeEllipsis: string;
    codeSent: string;
    codeSentToPhone: string;
    verificationCodeSent: string;
    emailFallbackCodeSent: string;
    checkFormAndTryAgain: string;
    somethingWentWrong: string;
    newUser: string;
    invitationFound: string;
    accountFound: string;
    accountFoundUseEmailSmsNotReady: string;
    smsLoginContactCareTeam: string;
    authMethodError: string;
    smsLoginUnavailable: string;
    email: string;
    emailTab: string;
    phoneTab: string;
    emailPlaceholder: string;
    password: string;
    phone: string;
    enterEmailAddress: string;
    enterPhoneNumber: string;
    enterPhoneWithCountryCode: string;
    forgotPasswordEmailHint: string;
    forgotPasswordPhoneHint: string;
    sending: string;
    backToSignIn: string;
    backToForgotPassword: string;
    backToConfirmAccount: string;
    backToSignup: string;
    verifyYourAccount: string;
    verifyEmailCodeHint: string;
    verifySmsCodeHint: string;
    verifying: string;
    resendCode: string;
    didNotReceiveCode: string;
    resendConfirmationCode: string;
    needHelpContact: string;
    consentTermsPrefix: string;
    termsOfUse: string;
    consentAnd: string;
    privacyPolicy: string;
    selectHospitalLabel: string;
    selectHospitalPlaceholder: string;
    selectHospitalToContinue: string;
    /** Study clinician invites: collect organisation name on signup (creates own org). */
    studyClinicianSignupHint: string;
    /** Study clinician invites that already pin an organisation. */
    studyClinicianSignupHintWithOrganisation: string;
    ownOrganisationNameLabel: string;
    ownOrganisationNamePlaceholder: string;
    ownOrganisationNameHint: string;
    organisationNameTooShort: string;
    signupSessionExpired: string;
    loadingSignup: string;
    welcomeToPatientWatch: string;
    invitedByLine: (name: string) => string;
    welcomeFirstTime: string;
    choosePasswordDescription: string;
    alreadyHavePasswordSkip: string;
    finishProfileSetup: string;
    continueToProfileSetup: string;
    alreadySignedIn: string;
    alreadySignedInDescription: string;
    continueToProfile: string;
    continueToApp: string;
    notLoggedInResetPassword: string;
    authenticationComplete: string;
    authenticationCompleteDescription: string;
    authenticationFailed: string;
    authenticationFailedDescription: string;
    noAccountFound: string;
    noAccountForContact: (contact: string) => string;
    noAccountForDetails: string;
    inviteOnlyPlatform: string;
    iAmPatient: string;
    iAmClinician: string;
    wrongEmailGoBack: string;
    goBackAndTryAgain: string;
    noAccountChoosePath: string;
    noAccountPatientTitle: string;
    noAccountPatientDescription: string;
    noAccountPatientCta: string;
    noAccountClinicianTitle: string;
    noAccountClinicianDescription: string;
    noAccountClinicianCta: string;
    confirmAccountInviteTitle: string;
    couldNotVerifyContact: string;
    invitedToJoin: string;
    inviterInvitedYou: (name: string) => string;
    confirmPhoneInviteBody: string;
    confirmEmailInviteBody: string;
    useInvitationContactDetails: string;
    enterInvitationContactHint: string;
    enterConfirmationCode: string;
    processing: string;
    verifyYourEmailTitle: string;
    verifyEmailIcon: string;
    emailConfirmed: string;
    emailConfirmedDescription: string;
    verifyEmailWelcome: (firstName?: string) => string;
    verifyEmailSendHint: string;
    sendingCodeButton: string;
    sendVerificationCodeButton: string;
    enterVerificationCode: string;
    enterCodeSentTo: (email: string) => string;
    checkJunkFolder: string;
    deleteAccount: string;
    deleteAccountReason: string;
    troubleAccessingTitle: string;
    patientInviteMismatch: (email: string) => string;
    patientContactProvider: string;
    contactSupportTeam: string;
    interestedInPwTitle: string;
    interestedInPwBody1: string;
    signupStarterPlan: string;
    carePlanInjection: string;
    carePlanInjectionTo: (where: string, substance: string, amount: string) => string;
    carePlanSurgery: string;
    carePlanSurgeryArea: (area: string) => string;
    carePlanShockwave: string;
    authErrorTitle: string;
    linkExpiredTitle: string;
    almostThere: string;
    preparingAccount: string;
    verifyEmailCallBody: (email: string) => string;
    verifyingPhone: string;
    verifyingEmail: string;
    phoneVerified: string;
    emailVerified: string;
    verifyingPhoneWait: string;
    verifyingEmailWait: string;
    phoneVerifiedSuccess: string;
    emailVerifiedSuccess: string;
    verifyPhoneError: string;
    verifyEmailError: string;
    verificationError: string;
    tryAgain: string;
    freemiumSignupTitle: string;
    freemiumSignupDescription: string;
    freemiumSignupBenefit1: string;
    freemiumSignupBenefit2: string;
    freemiumSignupBenefit3: string;
    freemiumSignupCta: string;
    freemiumSignupSubmitting: string;
    freemiumSignupUpgradeHint: string;
    freemiumSignupSeePaidPlans: string;
    diaryInviteUnavailableTitle: string;
    diaryInviteUnavailableCallout: string;
    diaryInviteUnavailableBody: string;
    diaryInviteUnavailableNextStep: string;
    diaryInviteOwnerUnresolvedBody: string;
    mfaEnrollTitle: string;
    mfaEnrollDescription: string;
    mfaManualSetup: string;
    mfaQrCodeAlt: string;
    mfaChallengeTitle: string;
    mfaChallengeDescription: string;
    mfaAuthenticatorCode: string;
    mfaVerifyCode: string;
    mfaVerifying: string;
    mfaLoading: string;
    mfaSettingsTitle: string;
    mfaSettingsDescription: string;
    mfaFactorActive: string;
    mfaReplaceFactor: string;
    mfaReplaceConfirm: string;
}

const authCopy: Record<SupportedLocale, AuthCopy> = {
    "en-GB": authCopyEn,
    "en-US": authCopyEnUs,
    "de-DE": authCopyDe,
    "da-DK": authCopyDa,
    "fr-FR": authCopyFr,
    "it-IT": authCopyIt,
    "es-ES": authCopyEs,
};

export function useAuthCopy(): AuthCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(authCopy, locale);
}

export function getAuthCopy(locale: SupportedLocale): AuthCopy {
    return pickAppCopy(authCopy, locale);
}
